import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * ID89(15-B-2) 노출 항목 설정.
 *
 * 상품 목록(ProductTable)에서 운영자가 화면에 표시할 컬럼을 직접 선택한다(고도몰 상품
 * 리스트의 "노출 항목" 관용). 어떤 컬럼을 보느냐는 서버 데이터가 아니라 운영자 개인의
 * 화면 환경설정이므로 **클라이언트 전용(localStorage)** 으로 보관한다 — BE 저장 불필요.
 * (be-support 진단 정합: 목록 컬럼 가시성은 per-operator UI 선호로 서버 영속 대상 아님.)
 */

/** 토글 가능한 선택(optional) 컬럼 id. name 은 항상 표시되는 필수 컬럼이라 제외.
 *  QA-R6 H: 'copy'(상품 복사) 추가 — 행 우측 복사 액션 컬럼도 노출 항목 토글로 켜고 끈다. */
export type ToggleableProductColumn =
  | 'optionCount'
  | 'productType'
  | 'artistName'
  | 'productCode'
  | 'barcode'
  | 'displayed'
  | 'saleStatus'
  | 'listPrice'
  | 'salePrice'
  | 'releaseDate'
  | 'createdAt'
  | 'totalStockQty'
  | 'isSoldOut'
  | 'copy';

/** 운영자가 끌 수 있는 컬럼의 표시 순서(목록 헤더 순서와 동일).
 *  QA-89(ID89) — 정가(listPrice) + 자체상품코드(productCode)/바코드(barcode)/노출상태(displayed) 컬럼
 *  추가(응답 ProductSummary 에 모두 존재 — be-catalog 66c1711).
 *  + QA-89 2차 — 판매상태(saleStatus)/옵션(optionCount).
 *  ★QA-R6 H: 컬럼 순서 재정렬 — 상품명(고정) 바로 뒤 옵션(optionCount), 바코드 뒤 노출상태(displayed)-판매상태(saleStatus).
 *    또한 행 복사(copy) 컬럼을 노출 항목 토글에 추가(기본 ON, 기존 항상-노출 동작 유지). 이 배열 순서가
 *    ProductTable 의 헤더/셀 렌더 순서 및 노출항목 메뉴 순서의 정본이다(둘이 어긋나면 가시성 토글이 깨짐). */
export const TOGGLEABLE_PRODUCT_COLUMNS: ReadonlyArray<ToggleableProductColumn> = [
  'optionCount',
  'productType',
  'artistName',
  'productCode',
  'barcode',
  'displayed',
  'saleStatus',
  'listPrice',
  'salePrice',
  'releaseDate',
  // 등록일/수정일 — 한 칸에 두 줄(고도몰 상품 리스트 '등록일/수정일' 관용).
  'createdAt',
  'totalStockQty',
  'isSoldOut',
  'copy',
];

/**
 * QA-89 — 저장값이 없는 신규 운영자의 기본 노출 컬럼. 기존 6컬럼만 켜 둔 상태로 유지해
 * 추가 컬럼(자체상품코드/바코드/노출상태)은 운영자가 "노출 항목 설정"에서 켤 때만 보이도록 한다
 * (기존 화면 폭 회귀 0). productCode/barcode/displayed 는 기본 OFF.
 * ★QA-R6 H: 'copy'(상품 복사) 는 기본 ON — 직전까지 항상 노출되던 행 복사 액션을 토글화하되
 *   기본값에 포함시켜 기존 동작(항상 보임)을 보존한다. optionCount 도 기본 ON(상품명 뒤 옵션 노출).
 */
export const DEFAULT_VISIBLE_COLUMNS: ReadonlyArray<ToggleableProductColumn> = [
  'optionCount',
  'productType',
  'artistName',
  'listPrice',
  'salePrice',
  'releaseDate',
  'createdAt',
  'totalStockQty',
  'isSoldOut',
  'copy',
];

const STORAGE_KEY = 'product-column-prefs';

/** 기본값: 추가 컬럼을 제외한 기존 7컬럼만 노출(기존 동작과 동일 — 회귀 0). */
const DEFAULT_VISIBLE: ReadonlyArray<ToggleableProductColumn> = [...DEFAULT_VISIBLE_COLUMNS];

const isToggleable = (value: unknown): value is ToggleableProductColumn =>
  typeof value === 'string' &&
  (TOGGLEABLE_PRODUCT_COLUMNS as ReadonlyArray<string>).includes(value);

/**
 * localStorage 에서 저장된 노출 컬럼을 읽는다. 저장값이 없거나 형식이 깨졌으면 기본값.
 * 알 수 없는 컬럼(스키마 변경 후 잔존 값)은 무시하고 알려진 컬럼만 채택한다.
 */
const readStored = (): ReadonlyArray<ToggleableProductColumn> => {
  if (typeof window === 'undefined') return DEFAULT_VISIBLE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE;
    const known = parsed.filter(isToggleable);
    // 저장 순서가 아니라 정규 컬럼 순서로 정렬해 헤더 순서를 안정화.
    return TOGGLEABLE_PRODUCT_COLUMNS.filter((c) => known.includes(c));
  } catch {
    return DEFAULT_VISIBLE;
  }
};

const persist = (columns: ReadonlyArray<ToggleableProductColumn>): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // localStorage 불가(프라이빗 모드/할당량) — 화면 동작은 유지하고 영속만 생략.
  }
};

export interface ProductColumnPrefsState {
  /** 현재 노출 중인 선택 컬럼(정규 순서). 항상 표시되는 name 은 포함하지 않는다. */
  visibleColumns: ReadonlyArray<ToggleableProductColumn>;
  /** 단건 토글 — 마지막 1개를 끄려 하면 무시(최소 1개 컬럼 유지). */
  toggleColumn: (column: ToggleableProductColumn) => void;
  /** 전체 노출로 복원(기본값). */
  resetColumns: () => void;
}

export const useProductColumnPrefs = create<ProductColumnPrefsState>()(
  devtools(
    (set) => ({
      visibleColumns: readStored(),
      toggleColumn: (column) =>
        set(
          (s) => {
            const has = s.visibleColumns.includes(column);
            // 마지막 1개까지 끄면 빈 표가 되므로, 최소 1개는 유지한다.
            if (has && s.visibleColumns.length <= 1) return s;
            const next = has
              ? s.visibleColumns.filter((c) => c !== column)
              : TOGGLEABLE_PRODUCT_COLUMNS.filter(
                  (c) => c === column || s.visibleColumns.includes(c),
                );
            persist(next);
            return { visibleColumns: next };
          },
          false,
          'toggleColumn',
        ),
      resetColumns: () =>
        set(
          () => {
            persist(DEFAULT_VISIBLE);
            return { visibleColumns: DEFAULT_VISIBLE };
          },
          false,
          'resetColumns',
        ),
    }),
    { name: 'product-column-prefs' },
  ),
);
